import React, {useState} from 'react';
import {errorToastOptions} from "./ErrorToast";
import {
    IonButton,
    IonIcon,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonList,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonToggle, useIonActionSheet, useIonLoading, useIonToast,
    useIonViewWillEnter
} from '@ionic/react';
import {getIpAccesses, patchIpAccess} from "../repositories/ipAccessRepository";
import {useInfiniteQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import moment from "moment/moment";
import {refresh, hourglassOutline} from "ionicons/icons";

// a 422 carries a message meant for the user (e.g. the service user name is missing in the target)
const getErrorMessage = (e, fallback) =>
    e.response?.status === 422 && typeof e.response.data === 'string' && e.response.data ? e.response.data : fallback;

const IpAccessEntityList = ({userId, serviceId, readonly}) => {
    const [searchText, setSearchText] = useState('');
    const queryClient = useQueryClient();
    const [presentActionSheet] = useIonActionSheet();
    const [presentLoading, dismissLoading] = useIonLoading();
    const [presentToast] = useIonToast();
    // IonToggle flips itself on click; remounting after a mutation settles resyncs it with the data
    const [toggleResetKey, setToggleResetKey] = useState(0);

    const ipAccessesQuery = useInfiniteQuery({
        queryKey: ['ipAccesses', 'search', {userId, serviceId, searchText}],
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => lastPage.length > 0 ? pages.length + 1 : undefined,
        queryFn: ({pageParam}) => getIpAccesses(userId, serviceId, searchText, pageParam),
        refetchInterval: 5000,
    });

    useIonViewWillEnter(() => {
        ipAccessesQuery.refetch();
    })

    const reload = async (event) => {
        await ipAccessesQuery.refetch();
        event.detail.complete();
    };

    const loadMore = async (event) => {
        await ipAccessesQuery.fetchNextPage();
        event.target.complete();
    };

    const handleSearch = async (event) => {
        setSearchText(event.detail.value);
    };

    const selectDuration = () => {
        return new Promise((resolve, reject) => {
            presentActionSheet({
                header: 'Access duration',
                buttons: [
                    {
                        text: 'Forever',
                        role: 'destructive',
                        handler: () => resolve('forever'),
                    },
                    {
                        text: '1 minute',
                        handler: () => resolve([1, 'minutes']),
                    },
                    {
                        text: '5 minutes',
                        handler: () => resolve([5, 'minutes'])
                    },
                    {
                        text: '1 hour',
                        handler: () => resolve([1, 'hours'])
                    },
                    {
                        text: '1 day',
                        handler: () => resolve([1, 'days'])
                    },
                    {
                        text: '1 week',
                        handler: () => resolve([1, 'weeks'])
                    },
                    {
                        text: '1 month',
                        handler: () => resolve([1, 'months'])
                    },
                    {
                        text: 'Cancel',
                        role: 'cancel',
                        handler: () => resolve('cancel')
                    },
                ]
            });
        });
    }

    const addNewIpAddress = async () => {
        try {
            const duration = await selectDuration();
            if (duration === 'cancel') {
                return;
            }
            await presentLoading({message: 'Granting IP address access'})
            await patchIpAccess(userId, serviceId, searchText, [
                {op: 'replace', path: '/enabled', value: true},
                {
                    op: 'replace',
                    path: '/revokeAt',
                    value: duration === 'forever' ? null : moment().utc().add(...duration).toISOString()
                },
            ]);
            await dismissLoading();
            await presentToast({message: "Successfully granted IP address access", duration: 2000, color: "success"});
        } catch (e) {
            console.error(e);
            await dismissLoading();
            await presentToast(errorToastOptions(getErrorMessage(e, "Failed to grant IP address access")));
            throw e;
        }
    }

    const addNewIpAddressMutation = useMutation({
        mutationFn: addNewIpAddress,

        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ['ipAccesses', 'search', {userId, serviceId, searchText}] });
            const previousValue = queryClient.getQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}]);

            return { previousValue };
        },

        // On error, revert to previous value
        onError: (err, newValue, context) => {
            queryClient.setQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}], context?.previousValue);
        },

        // Always refetch after error or success
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['ipAccesses', 'search'] });
        },
    })

    const toggle = async (ipAccess) => {
        if (ipAccess.enabled) {
            try {
                await presentLoading({message: 'Revoking IP address access'})
                await patchIpAccess(userId, serviceId, ipAccess.ipAddress, [
                    {op: 'replace', path: '/enabled', value: false}
                ]);
                await dismissLoading();
                await presentToast({message: "Successfully revoked IP address access", duration: 2000, color: "success"});
            } catch (e) {
                await dismissLoading();
                const errorMessage = e.response?.status === 401
                    ? "Unauthorized: You don't have permission to revoke this IP address access"
                    : getErrorMessage(e, "Failed to revoke IP address access");
                await presentToast(errorToastOptions(errorMessage));
                throw e;
            }
        } else {
            try {
                const duration = await selectDuration();
                if (duration === 'cancel') {
                    return;
                }
                await presentLoading({message: 'Granting IP address access'})
                await patchIpAccess(userId, serviceId, ipAccess.ipAddress, [
                    {op: 'replace', path: '/enabled', value: true},
                    {
                        op: 'replace',
                        path: '/revokeAt',
                        value: duration === 'forever' ? null : moment().utc().add(...duration).toISOString()
                    },
                ]);
                await dismissLoading();
                await presentToast({message: "Successfully granted IP address access", duration: 2000, color: "success"});
            } catch (e) {
                console.error(e);
                await dismissLoading();
                await presentToast(errorToastOptions(getErrorMessage(e, "Failed to grant IP address access")));
                throw e;
            }
        }
    }

    const toggleMutation = useMutation({
        mutationFn: toggle,

        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ['ipAccesses', 'search', {userId, serviceId, searchText}] });
            const previousValue = queryClient.getQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}]);

            return { previousValue };
        },

        // On error, revert to previous value
        onError: (err, newValue, context) => {
            queryClient.setQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}], context?.previousValue);
        },

        // Always refetch after error or success
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ['ipAccesses', 'search'] });
            setToggleResetKey(key => key + 1);
        },
    })

    const isValidIPv4 = ip =>
        /^(\d{1,3}\.){3}\d{1,3}$/.test(ip) &&
        ip.split('.').every(n => n >= 0 && n <= 255);

    const getAddIpAddressButton = () => {
        // search is a substring match, so the list may be non-empty while the exact address does not exist yet
        const exactMatchExists = ipAccesses.some(ipAccess => ipAccess.ipAddress === searchText);
        if (!readonly && !exactMatchExists && isValidIPv4(searchText)) {
            return <IonItem key='add new' button onClick={() => addNewIpAddressMutation.mutate()}>Add IP address '{searchText}'</IonItem>
        }

        if (ipAccesses.length > 0) {
            return null;
        }

        if (readonly) {
            return <IonItem key='no matching ip addresses'>No matching IP addresses found.</IonItem>
        }

        return <IonItem key='no matching ip addresses add new'>No matching IP addresses found. To add new, enter a valid IP address.</IonItem>
    }

    const getExpiresString = revokeAt => {
        return revokeAt > moment.utc() ? 'Expires' : 'Should have been expired';
    }


    const renderExpiredTag = access => {
        if (!access.modifiedProgrammatically && access.revokeAt == null) return <></>
        return <p style={{
            display: 'flex', flexDirection: 'row',
            alignItems: 'center'
        }}>
            <IonIcon style={{marginRight: '5px'}} icon={hourglassOutline}></IonIcon>
            <span>{access.revokeAt == null
                ? `Expired ${moment.utc(access.revokedAt).fromNow()}`
                : `${getExpiresString(moment.utc(access.revokeAt))} ${moment.utc(access.revokeAt).fromNow()}`}</span>
        </p>
    }

    const ipAccesses = ipAccessesQuery.data?.pages.flatMap(data => data);

    return (
            <>
                <IonRefresher slot="fixed" onIonRefresh={reload}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>

                <IonItemDivider sticky className="access-list-search-bar">
                    <IonSearchbar
                        placeholder="Search or add new IP address"
                        debounce={1000}
                        mode="ios"
                        enterkeyhint="search" inputmode="search" onIonInput={(ev) => handleSearch(ev)}></IonSearchbar>
                    <IonButton fill="clear" slot="end" onClick={() => ipAccessesQuery.refetch()}>
                        <IonIcon icon={refresh} />
                    </IonButton>
                </IonItemDivider>

                <IonList>
                    {ipAccesses?.map(entity => (
                        <IonItem button={!readonly} key={entity.ipAddress} onClick={() => !readonly && toggleMutation.mutate(entity)}>
                            <IonLabel>
                                <h3>{entity.ipAddress}</h3>
                                {renderExpiredTag(entity)}
                            </IonLabel>
                            <IonToggle key={toggleResetKey} disabled={readonly} slot="end" checked={entity.enabled}></IonToggle>
                        </IonItem>
                    ))}
                    {ipAccesses && getAddIpAddressButton()}
                </IonList>

                <IonInfiniteScroll onIonInfinite={loadMore} threshold="100px" disabled={!ipAccessesQuery.hasNextPage}>
                    <IonInfiniteScrollContent loadingText="Loading more..."></IonInfiniteScrollContent>
                </IonInfiniteScroll>
            </>
    );
};

export default IpAccessEntityList;